import React from 'react'
import classNames from 'classnames'
import { AccountCustomWorkflowRunSummary } from '../../../store/customWorkflow/customWorkflows'
import { WorkflowRunStatusBadge } from '../../../components/CustomWorkflows/WorkflowRunStatusBadge'
import { RunContextChip } from '../../../components/CustomWorkflows/runContext/RunContextChip'
import { Button, ButtonVariant } from '../../../components/Button/Button'
import styles from '../Workflows.module.scss'

export interface RunRowAction {
  label: string
  variant: ButtonVariant
  onClick: () => void
}

const TIMESTAMP_FORMATTER = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })

const formatTimestamp = (epochSeconds: number | null): string => (epochSeconds ? TIMESTAMP_FORMATTER.format(new Date(epochSeconds * 1000)) : '—')

interface WorkflowRunRowProps {
  run: AccountCustomWorkflowRunSummary
  action?: RunRowAction
  showSubRunBadge?: boolean
  expanded?: boolean
  onToggleExpand?: () => void
}

export const WorkflowRunRow: React.FC<WorkflowRunRowProps> = ({ run, action, showSubRunBadge, expanded, onToggleExpand }) => {
  const childCount = run.childCount ?? 0
  const expandable = childCount > 0 && !!onToggleExpand
  return (
    <div className={classNames('d-flex zocks-gap-2 p-3 align-items-center border border-1 zocks-border-color-gray-300 bg-white w-100', styles.runRow)}>
      <div className='d-flex justify-content-between align-items-center w-100 zocks-gap-2'>
        <div className='d-flex flex-column gap-1 flex-grow-1 mw-0'>
          <div className='d-flex align-items-center zocks-gap-2 mw-0'>
            <span className='paragraph-medium medium text-truncate'>{run.workflowName || run.workflowId}</span>
            {showSubRunBadge && run.isSubRun ? (
              <span className='paragraph-small px-2 py-1 rounded zocks-bg-gray-100 zocks-text-gray-600 flex-shrink-0'>Sub-run</span>
            ) : null}
          </div>
          <div className='paragraph-small zocks-text-gray-600'>
            Started {formatTimestamp(run.createdAt)}
            {run.finishedAt ? ` · Finished ${formatTimestamp(run.finishedAt)}` : ''}
          </div>
          <RunContextChip context={run.context} />
        </div>
        <div className='d-flex gap-2 align-items-center flex-shrink-0'>
          {expandable ? (
            <Button type='button' variant='borderless' className='px-2 py-1' onClick={onToggleExpand} aria-expanded={expanded}>
              <span className='paragraph-small d-flex align-items-center zocks-gap-1'>
                <i className={classNames('bi', expanded ? 'bi-chevron-down' : 'bi-chevron-right')} aria-hidden='true' />
                {childCount} sub-run{childCount === 1 ? '' : 's'}
              </span>
            </Button>
          ) : null}
          <WorkflowRunStatusBadge status={run.status} />
          {action ? (
            <Button type='button' variant={action.variant} className='px-2 py-1' onClick={action.onClick}>
              <span className='paragraph-small'>{action.label}</span>
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
